import React from 'react';
import { MaterialIcon } from './MaterialIcon';
import { GatedMenuItem } from './GatedMenuItem';
import { useCanManage, type AclEntityType } from '../../hooks/data/useEntityAccess';
import { useIsReadOnly } from '../../hooks/user/useWorkspaceRole';
import { useManagePermissionsAction } from '../../hooks/ui/useManagePermissionsAction';

interface ManageAccessMenuItemProps {
  entityType: AclEntityType;
  entityId: string | undefined;
  /**
   * Opens the ShareEntityDialog when the user is allowed to manage
   * access. Caller renders the dialog and owns its open/close state;
   * this component only decides whether and how to invoke this
   * callback via the gated click path.
   */
  onOpenDialog: () => void;
}

/**
 * Single source of truth for the "Manage access" kebab item that
 * appears on every collection / note / protocol / attachment menu.
 *
 * Encapsulates the full gating policy so callers can't drift:
 *   - workspace VIEWER role             → disabled (workspace-wide read-only)
 *   - tier denies privacyControls       → enabled; click fires upgrade modal
 *   - workspace ACL flag off            → enabled; click shows operator hint
 *   - caller lacks MANAGE on the entity → disabled with explanation
 *   - everything OK                     → enabled; click opens dialog
 *
 * The item is always rendered (discoverability + upsell path on FREE/PRO);
 * GatedMenuItem handles the visible-but-disabled state with a tooltip.
 */
export const ManageAccessMenuItem: React.FC<ManageAccessMenuItemProps> = ({
  entityType,
  entityId,
  onOpenDialog,
}) => {
  const isReadOnlyRole = useIsReadOnly();
  const canManageEntity = useCanManage(entityType, entityId);
  const manageAccess = useManagePermissionsAction({ openDialog: onOpenDialog });

  // When the dialog can't open at all (tier blocked or workspace ACL off)
  // the item stays enabled — the click handler routes to the upgrade modal
  // / toast so users discover the feature and the upsell path can drive
  // conversions. We only disable when the gate is *user-specific*: a
  // workspace viewer or someone without MANAGE on this entity. (canOpenDialog
  // already implies aclEnforced, so there's no separate aclEnforced check.)
  const isAllowed = !isReadOnlyRole && (!manageAccess.canOpenDialog || canManageEntity);

  const disabledReason = isReadOnlyRole
    ? 'You have read-only access to this workspace.'
    : 'Only people with manage access can change who can see this.';

  return (
    <GatedMenuItem
      // fontSize, not boxSize: a MaterialIcon's glyph scales with fontSize
      icon={<MaterialIcon name="lock_person" fontSize="18px" />}
      onClick={manageAccess.onClick}
      isAllowed={isAllowed}
      disabledReason={disabledReason}
    >
      Manage access
    </GatedMenuItem>
  );
};
